import { fork, put, call, take } from 'redux-saga/effects';

import { REQUEST } from 'redux-config/reduxHelpers';
import { acceptError } from 'common/_redux';

import { SubmissionError } from 'redux-form';
import * as actionTypes from './actions';
import * as api from '../api';

export function* getManagersSampleListSaga({ search }) {
  try {
    const { status, data } = yield call(api.getManagersSampleList, search);
    if (status < 300) {
      yield put(actionTypes.getManagersSampleList.success(data));
    } else {
      yield put(actionTypes.getManagersSampleList.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagersSampleList.failure());
  }
}

export function* getManagersSampleListWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.GET_MANAGERS_SAMPLE_LIST[REQUEST]);
    yield call(getManagersSampleListSaga, data);
  }
}

export function* getManagersInfoSaga({ managerId }) {
  try {
    const { status, data } = yield call(api.getManagerInfo, managerId);
    if (status < 300) {
      yield put(actionTypes.getManagerInfo.success(data));
    } else {
      yield put(actionTypes.getManagerInfo.failure(data));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagerInfo.failure());
  }
}

export function* getManagerInfoWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.GET_MANAGER_INFO[REQUEST]);
    yield call(getManagersInfoSaga, data);
  }
}

export function* getManagerSampleSaga({ search }) {
  try {
    const { status, data } = yield call(api.getManagerSample, search);
    if (status < 300) {
      yield put(actionTypes.getManagerSample.success(data));
    } else {
      yield put(actionTypes.getManagerSample.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagerSample.failure());
  }
}

export function* getManagerSampleWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.GET_MANAGER_SAMPLE[REQUEST]);
    yield call(getManagerSampleSaga, data);
  }
}

export function* createManagerSampleSaga(payload) {
  try {
    const { status, data } = yield call(api.createManagerSample, payload);
    if (status < 300) {
      yield put(actionTypes.createManagerSample.success(data));
      yield call(getManagerSampleSaga, { search: { user: payload.manager } });
    } else {
      const error = data.detail ? { _error: data.detail } : { _error: Object.values(data).flat() };
      yield put(actionTypes.createManagerSample.failure(new SubmissionError(error)));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(actionTypes.createManagerSample.failure());
  }
}

export function* createManagerSampleWatcherSaga() {
  while (true) {
    const { payload } = yield take(actionTypes.createManagerSample.REQUEST);
    yield call(createManagerSampleSaga, payload);
  }
}

export function* editManagerSampleSaga({ sampleId, manager, ...payload }) {
  try {
    const { status, data } = yield call(api.editManagerSample, sampleId, payload);
    if (status < 300) {
      yield put(actionTypes.editManagerSample.success(data));
      yield call(getManagerSampleSaga, { search: { user: manager } });
    } else {
      const error = data.detail ? { _error: data.detail } : { _error: Object.values(data).flat() };
      yield put(actionTypes.editManagerSample.failure(new SubmissionError(error)));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(actionTypes.editManagerSample.failure());
  }
}

export function* editManagerSampleWatcherSaga() {
  while (true) {
    const { payload } = yield take(actionTypes.editManagerSample.REQUEST);
    yield call(editManagerSampleSaga, payload);
  }
}

export function* getManagersGroupListSaga({ search }) {
  try {
    const { status, data } = yield call(api.getManagersGroupList, search);
    if (status < 300) {
      yield put(actionTypes.getManagersGroupList.success(data));
    } else {
      yield put(actionTypes.getManagersGroupList.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagersGroupList.failure());
  }
}

export function* getManagersGroupListWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.GET_MANAGERS_GROUP_LIST[REQUEST]);
    yield call(getManagersGroupListSaga, data);
  }
}

export function* createManagerGroupSaga({ groups, ...formValues }) {
  const managers = Object.values(groups).map(item => ({ id: item.manager }));
  const chiefs = Object.values(groups)
    .filter(item => item.chief === 'chief')
    .map(item => ({ id: item.manager }));
  const payload = { ...formValues, managers, chiefs };
  try {
    const { status, data } = yield call(api.createManagerGroup, payload);
    if (status < 300) {
      yield put(actionTypes.createManagerGroup.success(data));
    } else {
      const error = data.detail ? { _error: data.detail } : { _error: Object.values(data).flat() };
      yield put(actionTypes.createManagerGroup.failure(new SubmissionError(error)));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(actionTypes.createManagerGroup.failure());
  }
}

export function* createManagerGroupWatcherSaga() {
  while (true) {
    const { payload } = yield take(actionTypes.createManagerGroup.REQUEST);
    yield call(createManagerGroupSaga, payload);
  }
}

export function* getManagerGroupDetailsSaga({ managerGroupId }) {
  try {
    const { status, data } = yield call(api.getManagerGroupDetails, managerGroupId);
    if (status < 300) {
      yield put(actionTypes.getManagerGroupDetails.success(data));
    } else {
      yield put(actionTypes.getManagerGroupDetails.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagerGroupDetails.failure());
  }
}

export function* getManagerGroupDetailsWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.GET_MANAGER_GROUP_DETAILS[REQUEST]);
    yield call(getManagerGroupDetailsSaga, data);
  }
}

export function* editManagerGroupSaga({ managerGroupId, groups, ...formValues }) {
  const managers = Object.values(groups)
    .filter(item => item)
    .map(item => ({ id: item.manager }));
  const chiefs = Object.values(groups)
    .filter(item => item && item.chief === 'chief')
    .map(item => ({ id: item.manager }));
  const payload = { ...formValues, managers, chiefs };
  try {
    const { status, data } = yield call(api.editManagerGroup, managerGroupId, payload);
    if (status < 300) {
      yield put(actionTypes.editManagerGroup.success(data));
    } else {
      const error = data.detail ? { _error: data.detail } : { _error: Object.values(data).flat() };
      yield put(actionTypes.editManagerGroup.failure(new SubmissionError(error)));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(actionTypes.editManagerGroup.failure());
  }
}

export function* editManagerGroupWatcherSaga() {
  while (true) {
    const { payload } = yield take(actionTypes.editManagerGroup.REQUEST);
    yield call(editManagerGroupSaga, payload);
  }
}

export function* deleteManagerGroupSaga({ managerGroupId, search }) {
  try {
    const { status, data } = yield call(api.deleteManagerGroup, managerGroupId);
    if (status < 300) {
      yield put(actionTypes.deleteManagerGroup.success(data));
      yield call(getManagersGroupListSaga, { search });
    } else {
      yield put(actionTypes.deleteManagerGroup.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.deleteManagerGroup.failure());
  }
}

export function* deleteManagerGroupWatcherSaga() {
  while (true) {
    const data = yield take(actionTypes.DELETE_MANAGER_GROUP[REQUEST]);
    yield call(deleteManagerGroupSaga, data);
  }
}

export function* getManagerSalesStatusInfoSaga(salesStatusId) {
  try {
    const { status, data } = yield call(api.getManagerSalesStatusInfo, salesStatusId);
    if (status < 300) {
      yield put(actionTypes.getManagerSalesStatusInfo.success(data));
    } else {
      yield put(actionTypes.getManagerSalesStatusInfo.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagerSalesStatusInfo.failure());
  }
}

export function* getManagerSalesStatusInfoSagaWatcher() {
  while (true) {
    const { salesStatusId } = yield take(actionTypes.GET_MANAGER_SALES_STATUS_INFO[REQUEST]);
    yield call(getManagerSalesStatusInfoSaga, salesStatusId);
  }
}

export function* getManagerSalesStatusesSaga() {
  try {
    const { status, data } = yield call(api.getManagerSalesStatuses);
    if (status < 300) {
      yield put(actionTypes.getManagerSalesStatuses.success(data));
    } else {
      yield put(actionTypes.getManagerSalesStatuses.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getManagerSalesStatuses.failure());
  }
}

export function* getManagerSalesStatusesWatcherSaga() {
  while (true) {
    yield take(actionTypes.GET_MANAGER_SALES_STATUSES[REQUEST]);
    yield call(getManagerSalesStatusesSaga);
  }
}

export function* createSalesStatusSaga(payload) {
  try {
    const { status, data } = yield call(api.createSalesStatus, payload);
    if (status < 300) {
      yield put(actionTypes.createSalesStatus.success(data));
    } else {
      const error = data.detail ? { _error: data.detail } : { _error: Object.values(data).flat() };
      yield put(actionTypes.createSalesStatus.failure(new SubmissionError(error)));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(actionTypes.createSalesStatus.failure());
  }
}

export function* createSalesStatusWatcher() {
  while (true) {
    const { payload } = yield take(actionTypes.createSalesStatus.REQUEST);
    yield call(createSalesStatusSaga, payload);
  }
}

export function* deleteSalesStatusSaga({ id }) {
  try {
    const { status, data } = yield call(api.deleteSalesStatus, id);
    if (status < 300) {
      yield put(actionTypes.deleteSalesStatus.success(data));
      yield call(getManagerSalesStatusesSaga);
    } else {
      yield put(actionTypes.deleteSalesStatus.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.deleteSalesStatus.failure());
  }
}

export function* deleteSalesStatusSagaWatcher() {
  while (true) {
    const { payload } = yield take(actionTypes.deleteSalesStatus.REQUEST);
    yield call(deleteSalesStatusSaga, payload);
  }
}

export function* editSalesStatusSaga({ salesStatusId, ...payload }) {
  try {
    const { status, data } = yield call(api.editSalesStatus, salesStatusId, payload);
    if (status < 300) {
      yield put(actionTypes.editSalesStatus.success(data));
      yield call(getManagerSalesStatusesSaga);
    } else {
      yield put(actionTypes.editSalesStatus.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.editSalesStatus.failure());
  }
}

export function* editSalesStatusSagaWatcher() {
  while (true) {
    const { payload } = yield take(actionTypes.editSalesStatus.REQUEST);
    yield call(editSalesStatusSaga, payload);
  }
}

export function* getFiltersListSaga() {
  try {
    const { status, data } = yield call(api.getFiltersList);
    if (status < 300) {
      yield put(actionTypes.getFiltersList.success(data));
    } else {
      yield put(actionTypes.getFiltersList.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(actionTypes.getFiltersList.failure());
  }
}

export function* getFiltersListWatcherSaga() {
  while (true) {
    yield take(actionTypes.GET_FILTERS_LIST[REQUEST]);
    yield call(getFiltersListSaga);
  }
}

export default [
  fork(getManagersSampleListWatcherSaga),
  fork(getManagerInfoWatcherSaga),
  fork(createManagerSampleWatcherSaga),
  fork(getManagerSampleWatcherSaga),
  fork(editManagerSampleWatcherSaga),
  fork(getManagersGroupListWatcherSaga),
  fork(createManagerGroupWatcherSaga),
  fork(getManagerGroupDetailsWatcherSaga),
  fork(editManagerGroupWatcherSaga),
  fork(deleteManagerGroupWatcherSaga),
  fork(getManagerSalesStatusInfoSagaWatcher),
  fork(getManagerSalesStatusesWatcherSaga),
  fork(createSalesStatusWatcher),
  fork(deleteSalesStatusSagaWatcher),
  fork(editSalesStatusSagaWatcher),
  fork(getFiltersListWatcherSaga),
];
